import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink, ShieldAlert, ShieldCheck, GitMerge } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function DuplicateWarning({ duplicates, onDismiss, onProceed, onMerge, loading }) {
  if (!duplicates || duplicates.length === 0) return null;

  return (
    <div className="col-span-2 border border-amber-300 bg-amber-50 rounded-sm p-3 animate-slideIn" data-testid="duplicate-warning">
      <div className="flex items-start gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-amber-900">
            {duplicates.length} potential duplicate{duplicates.length > 1 ? 's' : ''} found
          </p>
          <p className="text-xs text-amber-700 mt-0.5">Review before creating to avoid duplicate entries.</p>
        </div>
      </div>
      <div className="space-y-2 ml-6">
        {duplicates.map((d) => (
          <div
            key={d.id}
            className="flex items-center justify-between p-2 bg-white border border-amber-200 rounded-sm"
            data-testid={`duplicate-match-${d.id}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {d.confidence === 'high'
                ? <ShieldAlert className="w-4 h-4 text-red-500 shrink-0" />
                : <ShieldCheck className="w-4 h-4 text-amber-500 shrink-0" />}
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{d.name}</p>
                <p className="text-[10px] text-slate-500 truncate">
                  {d.company && `${d.company} · `}{d.email || d.phone || ''}
                  <span className={`ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-semibold ${
                    d.confidence === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {d.confidence === 'high' ? 'HIGH' : 'MEDIUM'} — {d.match_reason}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0 ml-2">
              {onMerge && (
                <button
                  onClick={() => onMerge(d)}
                  className="text-[10px] text-[#0047AB] hover:underline flex items-center gap-0.5 font-medium"
                  data-testid={`merge-duplicate-${d.id}`}
                >
                  <GitMerge className="w-3 h-3" /> Merge
                </button>
              )}
              <Link
                to={`/leads/${d.id}`}
                target="_blank"
                className="text-[10px] text-slate-500 hover:underline flex items-center gap-0.5"
                data-testid={`view-duplicate-${d.id}`}
              >
                View <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3 ml-6">
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs rounded-sm border-amber-300 text-amber-700 hover:bg-amber-100"
          onClick={onDismiss}
          data-testid="duplicate-dismiss-btn"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs rounded-sm bg-amber-600 hover:bg-amber-700 text-white"
          onClick={onProceed}
          disabled={loading}
          data-testid="duplicate-proceed-btn"
        >
          {loading ? 'Creating...' : 'Create Anyway'}
        </Button>
      </div>
    </div>
  );
}
